/* The tester. One script for one page, bundled with the engine inside it.
 *
 * **It talks to the DOM through `data-rx` attributes only**, so restyling the
 * page cannot break behaviour and a hook that disappears is a failing test
 * rather than a control that quietly does nothing.
 *
 * **THE SAME FILE RUNS TWICE: once as the page's script and once as a Web
 * Worker made from its own URL.** The engine is synchronous WebAssembly and a
 * large pattern over a long subject is three compiles and a full scan per
 * keystroke; on the page's thread that is a caret that stops moving (the
 * owner's requirement, 2026-09-13). Which half runs is decided by whether
 * `document` exists: a worker has none. `client.js` is the page's side,
 * `worker.js` the engine's, and `engine.js` loads the WebAssembly lazily so
 * the page's copy never materialises the 400 KB it does not run.
 *
 * **It runs once and it is not idempotent by accident.** `App.razor` adds this
 * from the document rather than from a component (I-83: a script inside a
 * component that re-renders is inserted twice), and enhanced navigation is off
 * for the whole site, so the page really does load fresh. The guard below still
 * exists because that is one setting away from being untrue and the symptom
 * would be two listeners on every input.
 */

import { start, analyse, trace } from "./client.js";
import { serve } from "./worker.js";
import { segments, substitute } from "./highlight.js";
import { leading, LETTERS } from "./options.js";
import { buildTree, captureNames } from "./stackwalk.js";
import { draw } from "./diagram.js";
import { drawSearch } from "./flowchart.js";
import { PROPERTIES, standalone, resized, pngScale } from "./export.js";

if (typeof document === "undefined") {
  serve(self);
} else if (!window.__rxTester) {
  window.__rxTester = true;
  main();
}

function main() {
  const root = document.querySelector('[data-rx="tester"]');

  if (!root) {
    return; // Not this page.
  }

  const find = (name) => root.querySelector(`[data-rx="${name}"]`);

  const ui = {
    pattern: find("pattern"),
    subject: find("subject"),
    replacement: find("replacement"),
    highlight: find("highlight"),
    summary: find("summary"),
    groups: find("groups"),
    replaced: find("replaced"),
    diagram: find("diagram"),
    trace: find("trace"),
    traceStatus: find("trace-status"),
    flowchart: find("flowchart"),
    flowHint: find("flow-hint"),
    exportPattern: find("export-pattern"),
    exportPatternNote: find("export-pattern-note"),
    exportSteps: find("export-steps"),
    exportStepsNote: find("export-steps-note"),
    downloads: [...root.querySelectorAll('[data-rx="download"]')],
    error: find("error"),
    engine: find("engine"),
    flags: [...root.querySelectorAll('[data-rx="flag"]')],
    examples: [...root.querySelectorAll('[data-rx="example"]')],
  };

  // **The reader's OWN choices, kept apart from what the boxes show.** A box
  // the pattern has decided shows the pattern's answer and is locked; when the
  // pattern stops deciding it, the box goes back to what the reader had
  // chosen rather than to whatever the lock left behind. Seeded from the
  // markup because a browser restores checkbox state across a reload.
  const chosen = new Set(ui.flags.filter((flag) => flag.checked).map((flag) => flag.value));

  ui.summary.textContent = "Starting the engine.";

  start().then((built) => {
    ui.engine.textContent =
      `Running PCRE2 ${built.pcre2.split(" ")[0]} in your browser. Nothing you type leaves this page.`;

    for (const element of [ui.pattern, ui.subject, ui.replacement]) {
      element.addEventListener("input", schedule);
    }

    for (const flag of ui.flags) {
      // Only a live box can fire this, so a change is always the reader's.
      flag.addEventListener("change", () => {
        if (flag.checked) {
          chosen.add(flag.value);
        } else {
          chosen.delete(flag.value);
        }

        run();
      });
    }

    for (const example of ui.examples) {
      example.addEventListener("click", () => {
        ui.pattern.value = example.dataset.pattern;
        ui.subject.value = example.dataset.subject;

        chosen.clear();
        for (const letter of example.dataset.flags) {
          chosen.add(letter);
        }

        run();

        // **A new pattern starts at the beginning.** The diagram panel scrolls
        // sideways for a wide pattern, and the scroll survives its contents
        // being replaced - so switching from a wide example to a narrow one left
        // the reader looking at empty space, or at the middle of something they
        // had not asked for. The subject box has the same problem for the same
        // reason, so both go back to the start.
        //
        // **Deliberately NOT done on every redraw.** Typing changes the pattern
        // too, and yanking the view back while somebody is editing the far end
        // of a long pattern would be worse than the bug. Switching examples is
        // an explicit jump; a keystroke is not.
        ui.diagram.scrollLeft = 0;
        ui.subject.scrollTop = 0;
        ui.subject.scrollLeft = 0;
        ui.highlight.scrollTop = 0;
        ui.highlight.scrollLeft = 0;

        ui.subject.focus();
      });
    }

    ui.trace.addEventListener("click", runTrace);

    for (const button of ui.downloads) {
      button.addEventListener("click", () => download(button.dataset.of, button.dataset.format));
    }

    // The subject scrolls; the highlight layer behind it has to scroll with it,
    // or the marks slide off the words they belong to.
    ui.subject.addEventListener("scroll", follow);

    run();
  }).catch((failure) => {
    ui.summary.textContent = "";
    show(ui.error, "The engine did not start, so this page cannot test anything. Reloading may help.");
    console.error("[regexray] the engine failed to start", failure);
  });

  /* **Debounced, because every keystroke is a full compile and a full scan of
     the subject.** 90 ms is below the point where typing feels laggy and above
     the point where a held key runs the engine per repeat. */
  let pending = null;

  // The last successful result, kept so that pointing at a cell can repaint the
  // highlight without running the engine again. Hovering is not a new question.
  let current = null;

  // **Which state of the form an answer is for.** The engine answers from a
  // worker, so an answer can arrive after the form has moved on; one that does
  // is dropped rather than painted over a newer state. Bumped by every run.
  let generation = 0;

  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(run, 90);
  }

  /**
   * Put the highlight layer where the textarea is.
   *
   * **Called after EVERY repaint as well as on scroll, and the order is the
   * whole point.** A paste scrolls the box to the caret at once, while the
   * layer behind gets its content 90 ms later; the scroll event fired while
   * the layer was still empty, so the copied offset clamped to zero and stayed
   * there until the reader scrolled by hand (the owner, 2026-09-14). A layer
   * that has just been given its content has to be told again where to be.
   */
  function follow() {
    ui.highlight.scrollTop = ui.subject.scrollTop;
    ui.highlight.scrollLeft = ui.subject.scrollLeft;
  }

  /**
   * **Point at a cell, see where it is in the text.** The owner's request,
   * 2026-09-09, and the reason it earns its place: the group table says WHAT
   * matched and the highlight says WHERE, and nothing joined the two - on a
   * subject with forty matches, finding the span a row referred to was a manual
   * search.
   *
   * It repaints from the LAST RESULT rather than matching again. Pointing at a
   * row is not a new question, and running the engine on every mouse move would
   * be the opposite of the rule this project keeps about work per interaction.
   */
  function emphasise(spans) {
    if (!current) {
      return;
    }

    ui.highlight.replaceChildren(...paint(current.subject, current.found.matches, spans));
    follow();
  }

  /**
   * **And click to select it in the box.** Focusing the textarea scrolls the
   * selection into view, which is most of the value on a long subject.
   */
  function select(span) {
    ui.subject.focus();
    ui.subject.setSelectionRange(span.start, span.end);
  }

  // What the last trace found, so it can stay visible when no step is being
  // pointed at. **Without this the debugger reports that it matched and never
  // shows WHICH**, which is the thing the reader pressed the button for.
  let traced = null;

  // Which press of the button a trace answer belongs to, for the same reason
  // as `generation`: a second press while the first is still running means
  // the first answer is no longer wanted.
  let tracing = 0;

  function clearTrace() {
    ui.flowchart.innerHTML = "";
    ui.exportSteps.hidden = true;
    ui.exportStepsNote.textContent = "";
    ui.flowHint.textContent = "";
    ui.traceStatus.textContent = "";
    traced = null;
  }

  /**
   * **What the engine actually did.** A separate, slower run: tracing needs
   * PCRE2's start up optimisations off, without which it can decline to start
   * at all - and the trace would then show nothing exactly when the reader most
   * wants to know why there was no match.
   */
  async function runTrace() {
    clearTrace();

    const pattern = ui.pattern.value;

    if (pattern.length === 0) {
      ui.traceStatus.textContent = "Type a regular expression first.";
      return;
    }

    const asked = generation;
    const mine = ++tracing;
    const { pattern: sent, flags } = compose(pattern);
    const subject = ui.subject.value;

    ui.traceStatus.textContent = "Tracing.";

    const walked = await trace(sent, subject, flags);

    // The form moved on, or the button was pressed again, while the engine
    // was working: this answer is about a state nobody is looking at.
    if (asked !== generation || mine !== tracing) {
      return;
    }

    if (walked.error) {
      ui.traceStatus.textContent = walked.error;
      return;
    }

    // **Counted from the attempt structure, not from a flag on the step.** The
    // engine starting again one character later moves the position backwards
    // and is NOT a backtrack; only a move backwards WITHIN one attempt is. The
    // status line read "0 backtracks" for a pattern that plainly backtracks
    // while this still counted a field that had been replaced, which is exactly
    // the sort of thing a green suite does not notice.
    const backtracks = countBacktracks(walked.steps);

    traced = walked.matches;

    // **Say WHAT it found, not only that it found something.** "The engine
    // finished with match" is a fact about our vocabulary; the text it matched
    // is the answer the reader pressed the button for.
    const outcome = walked.truncated
      ? "Stopped early: this pattern takes more steps than we will show."
      : walked.matches.length > 0
        ? `It found ${count(walked.matches.length, "match")}.`
        : walked.outcome === "no match"
          ? "It found no match."
          : `It stopped: ${walked.outcome}.`;

    ui.traceStatus.textContent =
      `${count(walked.steps.length, "step")}, ${count(backtracks, "backtrack")}. ${outcome}`;

    // **THE PICTURE IS THE WHOLE OF IT NOW.** A waterfall of the search and a
    // full step list stood beside this until 2026-09-10; the owner said neither
    // was needed once the flowchart carried what they said, and they were
    // deleted rather than left switched off.
    //
    // The traced pattern, not the reader's: item spans index the string the
    // engine was actually given, which carries the (*UCP) verb when that option
    // is ticked.
    const drawn = drawSearch(walked, sent, subject);

    ui.flowchart.innerHTML = drawn.svg;
    ui.exportSteps.hidden = drawn.svg.length === 0;
    ui.flowHint.textContent = drawn.truncated
      ? `Showing the first ${count(drawn.drawn, "attempt")} in full. `
        + "There are more than this picture can hold."
      : hint(drawn, walked);

    wireBoxes();

    // Leave every match ringed, so the answer is on the screen rather than only
    // in the sentence above it.
    emphasise(traced);
  }

  /**
   * Say what the picture is not drawing, and why.
   *
   * **Most of a search is failure**, and the bands say so; this says it once at
   * the top so a reader is not left wondering where the other thirty attempts
   * went.
   */
  function hint(drawn, walked) {
    const attempts = drawn.sections.reduce(
      (n, section) => n + (section.kind === "skipped" ? section.attempts.length : 1), 0);

    if (attempts === drawn.drawn) {
      return "";
    }

    return `${count(attempts, "attempt")} in all. `
      + `${count(drawn.drawn, "attempt")} drawn in full`
      + (walked.matches.length > 0
        ? ", the ones that found something."
        : ", the one that got furthest.");
  }

  /**
   * Point at a box: light the same item in the PATTERN DIAGRAM, and the text
   * that part of the pattern took from the subject.
   *
   * **This is what makes it a picture of the reader's own data.** A box knows
   * the spans it consumed, the diagram box knows the pattern text it stands
   * for, and the highlight layer knows how to ring a span - so pointing at one
   * part of the pattern shows exactly what it ate, in three places at once,
   * with no lookup table between any of them.
   */
  function wireBoxes() {
    for (const box of ui.flowchart.querySelectorAll("[data-start]")) {
      const item = { start: Number(box.dataset.start), end: Number(box.dataset.end) };

      const took = (box.dataset.took ?? "")
        .split(",")
        .filter((pair) => pair.length > 0)
        .map((pair) => {
          const [from, to] = pair.split(":");
          return { start: Number(from), end: Number(to) };
        });

      const on = () => {
        lightDiagram(item);
        // A box that consumed nothing has nothing to point at, and the matches
        // are a better answer than an arbitrary position.
        emphasise(took.length > 0 ? took : traced);
      };

      const off = () => {
        lightDiagram(null);
        emphasise(traced);
      };

      box.addEventListener("mouseenter", on);
      box.addEventListener("mouseleave", off);
    }
  }

  /**
   * Light one item in the pattern diagram, or none.
   *
   * **A flowchart box points at an item but not at a position**, which is the
   * difference between it and a step: a box stands for every visit the engine
   * made to that part of the pattern, so there is no one character under the
   * head to ring. It leaves the matches ringed instead, which is the honest
   * answer rather than the first position of many.
   */
  function lightDiagram(item) {
    for (const lit of ui.diagram.querySelectorAll(".rx-d-stepping")) {
      lit.classList.remove("rx-d-stepping");
    }

    if (!item) {
      return;
    }

    const box = ui.diagram.querySelector(
      `[data-start="${item.start}"][data-end="${item.end}"]`);

    if (box) {
      box.classList.add("rx-d-stepping");
    }
  }

  /**
   * **A box the pattern has decided is shown decided and locked.** The
   * pattern's own opening is read here on the page's thread, before the
   * engine is asked anything, because it is a few characters of prefix and
   * the boxes should settle with the keystroke rather than after the answer.
   * `options.js` has why this is a lexer and not a call into PCRE2.
   *
   * A locked box shows the pattern's value; a live one shows the reader's.
   * The reader's choice is remembered either way, so deleting the `(?i)`
   * hands the box back exactly as they left it.
   */
  function showFlags(control) {
    for (const flag of ui.flags) {
      const letter = flag.value;
      const locked = control.set.includes(letter) || control.cleared.includes(letter);

      flag.disabled = locked;
      flag.checked = locked ? control.set.includes(letter) : chosen.has(letter);
      flag.title = locked ? "The pattern sets this itself." : "";

      // A disabled box is drawn at 20% by the framework, which reads as absent
      // rather than as decided; `.rx-locked` in app.css keeps it legible.
      if (locked) {
        flag.classList.add("rx-locked");
      } else {
        flag.classList.remove("rx-locked");
      }
    }
  }

  /**
   * What the engine is given: the pattern, plus the reader's flags as the
   * letters the engine reads.
   *
   * **UCP is a pattern verb, not a flag letter**, because PCRE2 has no letter
   * for it. Prefixing the pattern is what the reader would type by hand, and
   * it keeps the engine's flag handling to the letters people know. Not
   * prefixed when the pattern already carries its own `(*UCP)`, which would
   * otherwise be sent twice.
   */
  function compose(pattern) {
    const control = leading(pattern);
    const verb = chosen.has("P") && !control.set.includes("P") ? "(*UCP)" : "";
    const flags = [...LETTERS].filter((letter) => letter !== "P" && chosen.has(letter)).join("");

    return { pattern: verb + pattern, flags, verb, control };
  }

  async function run() {
    const mine = ++generation;
    const pattern = ui.pattern.value;
    const subject = ui.subject.value;

    // **A trace of the PREVIOUS pattern is worse than no trace**, because every
    // step of it points at text that has moved. It goes the moment anything it
    // was about changes.
    clearTrace();

    const composed = compose(pattern);
    showFlags(composed.control);

    if (pattern.length === 0) {
      show(ui.error, "");
      ui.groups.replaceChildren();
      ui.summary.textContent = "Type a regular expression to begin.";
      ui.highlight.textContent = subject;
      follow();
      ui.replaced.textContent = "";
      clearDiagram();
      current = null;
      return;
    }

    const answer = await analyse(composed.pattern, subject, composed.flags);

    // Superseded before it was sent, or the form moved on while the engine
    // was working. Nothing on the page is cleared for a question that was
    // withdrawn: the newer run paints over it.
    if (answer === null || mine !== generation) {
      return;
    }

    show(ui.error, "");
    ui.groups.replaceChildren();

    if (answer.error) {
      show(ui.error, "The engine could not answer. Reloading may help.");
      console.error("[regexray] the engine failed", answer.error);
      return;
    }

    const found = answer.found;

    if (found.error) {
      // The offset is into the pattern the ENGINE saw, which carries the verb;
      // subtracting it puts the caret back on what the reader typed.
      const at = Math.max(0, found.at - composed.verb.length);
      show(ui.error, describeError(found.error, at, pattern));
      ui.summary.textContent = "";
      ui.highlight.textContent = subject;
      follow();
      ui.replaced.textContent = "";
      // The last good diagram is CLEARED rather than left standing. A picture
      // of the previous pattern beside an error about this one is worse than no
      // picture: it reads as though the broken pattern drew it.
      clearDiagram();
      return;
    }

    current = { subject, found };

    // The tree is built ONCE from the enumeration and serves two readers: the
    // table names its columns from it, and the diagram is drawn from it.
    const tree = treeOf(composed.pattern, answer.items, composed.verb.length);

    ui.summary.textContent = summarise(found, subject, answer.items);
    ui.highlight.replaceChildren(...paint(subject, found.matches));
    follow();
    ui.groups.replaceChildren(
      ...groupTable(found, subject, tree ? captureNames(tree) : {}, emphasise, select));
    ui.replaced.textContent = substitute(subject, found.matches, ui.replacement.value);
    drawDiagram(tree);
  }

  /**
   * The pattern's structure, from the engine's own enumeration, or null if it
   * could not be built.
   *
   * The verb prefix is stripped back off the spans, so a tree of a pattern
   * with Unicode properties turned on describes what the reader wrote rather
   * than an extra `(*UCP)` box they did not.
   */
  function treeOf(pattern, items, verbLength) {
    if (items.error) {
      return null;
    }

    const spans = items.items
      .map((item) => ({ pos: item.start - verbLength, len: item.end - item.start }))
      .filter((item) => item.pos >= 0);

    try {
      return buildTree(pattern.slice(verbLength), spans);
    } catch (failure) {
      console.error("[regexray] could not read the pattern's structure", failure);
      return null;
    }
  }

  /**
   * The picture of the pattern. **This is the reason the page exists**, so a
   * failure to draw one is reported rather than swallowed: an empty panel where
   * a diagram should be is the kind of thing that survives for months.
   */
  function drawDiagram(tree) {
    if (tree === null) {
      clearDiagram();
      return;
    }

    try {
      ui.diagram.innerHTML = draw(tree).svg;
      ui.exportPattern.hidden = false;
    } catch (failure) {
      clearDiagram();
      console.error("[regexray] could not draw the pattern", failure);
    }
  }

  // The download row goes with the picture: there is nothing to download from
  // an empty panel, and a stale note under a fresh drawing would be about the
  // wrong file.
  function clearDiagram() {
    ui.diagram.innerHTML = "";
    ui.exportPattern.hidden = true;
    ui.exportPatternNote.textContent = "";
  }

  /**
   * Save the picture the reader is looking at, in the theme they are looking
   * at it in. The styles are frozen from what the browser computed, so a dark
   * theme reader gets a dark file with a dark backing rather than pale text on
   * nothing; `export.js` has the rest of the reasoning.
   *
   * **On a press, never on a redraw.** Freezing walks the drawing and asks the
   * browser about every class in it, which is work nobody asked for until they
   * asked for the file.
   */
  function download(of, format) {
    const panel = of === "steps" ? ui.flowchart : ui.diagram;
    const note = of === "steps" ? ui.exportStepsNote : ui.exportPatternNote;
    const name = `regexray-${of}.${format}`;

    note.textContent = "";

    let file;

    try {
      file = standalone(panel.innerHTML, computedStyles(panel), backgroundOf(panel));
    } catch (failure) {
      note.textContent = "Could not prepare the picture for download.";
      console.error("[regexray] could not export", failure);
      return;
    }

    if (format === "svg") {
      save(new Blob([file.svg], { type: "image/svg+xml" }), name);
      return;
    }

    note.textContent = "Making the PNG.";

    rasterise(file).then((blob) => {
      note.textContent = "";
      save(blob, name);
    }).catch((failure) => {
      note.textContent = "Could not make a PNG in this browser. The SVG will still download.";
      console.error("[regexray] could not rasterise", failure);
    });
  }
}

/* What the file may leave out: SVG's own defaults, and the font properties on
   anything that draws no text. Every element would otherwise carry nine
   properties, most of them saying nothing, and the file triples in size. */
const SVG_DEFAULTS = {
  "stroke": "none",
  "stroke-dasharray": "none",
  "stroke-linecap": "butt",
  "stroke-linejoin": "miter",
  "font-weight": "400",
};

/**
 * What each class in a drawing computed to, read from the live elements.
 *
 * **Colours are normalised by drawing them**, because a browser reports a
 * `color-mix()` as `oklab(...)`, Illustrator, Inkscape and most image
 * libraries read no such thing, and a canvas fill style hands the same text
 * straight back (measured in Chrome 2026-09-13; the `#rrggbb` serialisation
 * only applies to colours that were sRGB to begin with). Painting one pixel
 * and reading it back gives red, green, blue and alpha in sRGB, which is what
 * every reader understands and what the page composites in anyway. Double
 * quotes in a font stack would end the style attribute early, so they become
 * single quotes.
 */
function computedStyles(panel) {
  const memo = new Map();
  const ink = pixel();

  return (tag, classes) => {
    // Groups carry only hover and cursor rules; the shapes inside them carry
    // their own paint.
    if (tag === "g") {
      return "";
    }

    const key = `${tag}.${classes}`;

    if (memo.has(key)) {
      return memo.get(key);
    }

    const element = [...panel.querySelectorAll(tag)]
      .find((candidate) => candidate.getAttribute("class") === classes);

    let style = "";

    if (element) {
      const computed = getComputedStyle(element);
      const draws = tag === "text" || tag === "tspan";
      const parts = [];

      for (const property of PROPERTIES) {
        let value = computed.getPropertyValue(property).trim();

        if (value.length === 0 || SVG_DEFAULTS[property] === value || (!draws && property.startsWith("font"))) {
          continue;
        }

        // `context-stroke` is a keyword, not a colour: it tells a marker to
        // take the stroke of the path it sits on, and it survives in the file
        // as written. Painting it would read back whatever the canvas held.
        if ((property === "fill" || property === "stroke") && value !== "none"
            && !value.startsWith("context-")) {
          value = normaliseColour(ink, value);
        }

        parts.push(`${property}:${value.replace(/"/g, "'")}`);
      }

      style = parts.join(";");
    }

    memo.set(key, style);
    return style;
  };
}

function backgroundOf(panel) {
  return normaliseColour(pixel(), getComputedStyle(panel).backgroundColor);
}

function pixel() {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  return canvas.getContext("2d", { willReadFrequently: true });
}

function normaliseColour(ink, value) {
  ink.clearRect(0, 0, 1, 1);
  ink.fillStyle = value;
  ink.fillRect(0, 0, 1, 1);

  const [r, g, b, a] = ink.getImageData(0, 0, 1, 1).data;

  return a === 255
    ? `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`
    : `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
}

/**
 * The standalone SVG drawn into a canvas at PNG_SCALE pixels per unit, or as
 * close to that as the browser allows for a drawing this size.
 *
 * The SVG handed to the image carries the SCALED width and height, not the
 * canvas: Safari rasterises an SVG image at its own declared size and scales
 * the pixels to fit the canvas, so a large canvas over a small SVG comes out
 * blurred. Declaring the size on the document makes every engine rasterise the
 * vector at that size.
 */
function rasterise(file) {
  const scale = pngScale(file.width, file.height);
  const width = Math.round(file.width * scale);
  const height = Math.round(file.height * scale);
  const url = URL.createObjectURL(new Blob([resized(file.svg, width, height)], { type: "image/svg+xml" }));

  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(image, 0, 0, width, height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("the canvas gave no image"))), "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("the drawing did not load as an image"));
    };

    image.src = url;
  });
}

function save(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked later rather than now: Firefox and Safari start the download
  // asynchronously, and revoking on the same tick can hand them a dead URL.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function show(element, text) {
  element.textContent = text;
}

/* Errors come out of PCRE2 in PCRE2's words. They are good words, but they
   describe a pattern rather than a mistake, so the position is what turns one
   into something actionable. */
function describeError(message, at, pattern) {
  if (at > 0 && at <= pattern.length) {
    return `${message} (at character ${at} of the pattern)`;
  }

  return message;
}

function summarise(found, subject, items) {
  const count = found.matches.length;

  if (count === 0) {
    return subject.length === 0 ? "Add some text to test against." : "No matches.";
  }

  const parts = [count === 1 ? "1 match" : `${count} matches`];

  if (found.captures > 0) {
    parts.push(found.captures === 1 ? "1 group" : `${found.captures} groups`);
  }

  if (!items.error) {
    // The end marker is an item with no width and is not a part of anything.
    const real = items.items.filter((item) => item.end > item.start).length;
    parts.push(real === 1 ? "1 part in the pattern" : `${real} parts in the pattern`);
  }

  return parts.join(", ") + ".";
}

/** The runs from `highlight.js`, turned into nodes. Rendering only. */
function paint(subject, matches, emphasis = null) {
  const nodes = segments(subject, matches, emphasis).map((run) => {
    // Text outside every match can still be emphasised: a lookaround group can
    // report a span the match does not cover.
    if (run.match < 0 && !run.emphasis) {
      return document.createTextNode(run.text);
    }

    const mark = document.createElement("mark");
    const classes = ["rx-mark", tint(run.group)];

    if (run.match < 0) classes.push("rx-outside");
    if (run.group > 0) classes.push("rx-group");
    if (run.emphasis) classes.push("rx-focus");

    mark.className = classes.join(" ");
    mark.dataset.group = String(run.group);
    mark.textContent = run.text;
    return mark;
  });

  // A trailing newline in a textarea is not shown by the browser unless
  // something follows it, so the layer behind would be one line short.
  nodes.push(document.createTextNode("\n"));

  return nodes;
}

/**
 * One row per match, one column per group.
 *
 * **Every cell that stands for a span is a real `<button>`**, which is what
 * makes pointing at it work from the keyboard as well as the mouse: focus and
 * blur do what hover and unhover do, and Enter selects. Making the `<td>` itself
 * clickable would have been less markup and would have left the whole feature
 * unreachable without a pointer, which this project's accessibility rule does
 * not allow.
 *
 * A group that did not participate has no span, so it is plain text: a control
 * that cannot do anything is worse than no control.
 *
 * **A pattern with no groups still lists its matches**, as one column of the
 * matched text. Until 2026-09-14 the table was built only when there were
 * groups, so "40 matches" stood over an empty space, and a pattern edited down
 * to no groups made the list vanish, which the owner read as the list not
 * refreshing. Twice. It is the list of matches first and the groups second.
 */
function groupTable(found, subject, names, emphasise, select) {
  if (found.matches.length === 0) {
    return [];
  }

  const table = document.createElement("table");
  table.className = "rx-table";

  // **Each header carries the tint of the spans in its column, as a dot
  // after the name**, so the eye joins "Group 2" to the text without reading
  // a legend. The owner's call, 2026-09-14, along with naming the columns
  // rather than numbering them; `.rx-swatch` in app.css draws the dot.
  const head = table.createTHead().insertRow();
  head.appendChild(cell("th", "Match", `rx-swatch ${tint(0)}`));

  if (found.captures === 0) {
    head.appendChild(cell("th", "Text", `rx-swatch ${tint(0)}`));
  }

  // **A named group is headed by its name**, the owner's call 2026-09-14:
  // `(?<local>...)@(?<domain>...)` reads as "local" and "domain", which is
  // what the person who wrote the pattern called them. Unnamed groups keep
  // their number.
  for (let g = 1; g <= found.captures; g++) {
    head.appendChild(cell("th", names[g] ?? `Group ${g}`, `rx-swatch ${tint(g)}`));
  }

  const body = table.createTBody();

  // A long subject with thousands of matches would build a table nobody can
  // read and a page that stalls laying it out. The count is still reported.
  const shown = found.matches.slice(0, 200);

  shown.forEach((groups, index) => {
    const row = body.insertRow();

    // The row's own number stands for the WHOLE match, so pointing at it shows
    // the match rather than any one group. That is the row a reader points at
    // first.
    row.appendChild(spanCell(String(index + 1), groups[0], subject, emphasise, select));

    if (found.captures === 0) {
      const whole = groups[0];
      row.appendChild(spanCell(subject.slice(whole.start, whole.end), whole, subject, emphasise, select));
    }

    for (let g = 1; g <= found.captures; g++) {
      const span = groups[g];

      if (!span) {
        const td = cell("td", "not used");
        td.className = "rx-unset";
        row.appendChild(td);
        continue;
      }

      row.appendChild(spanCell(subject.slice(span.start, span.end), span, subject, emphasise, select));
    }
  });

  if (found.matches.length > shown.length) {
    const row = body.insertRow();
    const td = cell("td", `${found.matches.length - shown.length} more matches are not listed here.`);
    td.colSpan = found.captures + 1;
    row.appendChild(td);
  }

  return [table];
}

/**
 * How many times the engine gave text back INSIDE an attempt.
 *
 * A step whose position is behind the previous one is only a backtrack when
 * both belong to the same attempt; across attempts it is the ordinary bump
 * along of any search.
 */
function countBacktracks(steps) {
  let backtracks = 0;

  for (let i = 1; i < steps.length; i++) {
    if (steps[i].from === steps[i - 1].from && steps[i].at < steps[i - 1].at) {
      backtracks += 1;
    }
  }

  return backtracks;
}

// "match" is the one noun here whose plural is not an s, and the status line
// read "3 matchs" for three days before anybody noticed.
const count = (n, noun) => `${n} ${n === 1 ? noun : noun === "match" ? "matches" : `${noun}s`}`;

/** A cell whose text stands for a span of the subject. */
function spanCell(text, span, subject, emphasise, select) {
  const td = document.createElement("td");

  const button = document.createElement("button");
  button.type = "button";
  button.className = "rx-span";
  // A zero width group has nothing to show, and an empty button is invisible
  // and unclickable. The marker says there IS something there and that it
  // matched nothing.
  button.textContent = text.length > 0 ? text : "\u2205";
  button.dataset.start = String(span.start);
  button.dataset.end = String(span.end);
  button.title = "Show this in the text, or click to select it";

  const on = () => emphasise(span);
  const off = () => emphasise(null);

  // Focus and blur alongside the pointer events, so a reader tabbing through
  // the table sees exactly what a reader hovering it sees.
  button.addEventListener("mouseenter", on);
  button.addEventListener("focus", on);
  button.addEventListener("mouseleave", off);
  button.addEventListener("blur", off);
  button.addEventListener("click", () => select(span));

  td.appendChild(button);
  return td;
}

function cell(tag, text, className = "") {
  const element = document.createElement(tag);
  element.textContent = text;
  element.className = className;
  return element;
}

/**
 * The class that colours a span or its column: the whole match is tint 0,
 * and capture groups take the six group tints in turn, so group 7 shares
 * with group 1. Seven distinct tints is more than a reader can tell apart
 * anyway; the header carrying the same one is what makes it legible.
 */
function tint(group) {
  return `rx-tint-${group === 0 ? 0 : ((group - 1) % 6) + 1}`;
}
